import { FC, useCallback } from 'react';
import { useFragment, useMutation } from 'react-relay';
import { Formik } from 'formik';
import graphql from 'babel-plugin-relay/macro';

import Stack from '@mui/material/Stack';
import Form from 'components/Form';
import AccordionController from 'components/AccordionController';
import Section from 'components/Section';
import TextField from 'components/TextField';
import MenuItemTypeSelect from 'components/MenuItemTypeSelect';
import IconSelect from 'components/IconSelect';

import { useErrorHandler } from 'lib';

import {
  MenuItemType,
  MenuItemsEditFormMutation,
} from '__generated__/MenuItemsEditFormMutation.graphql';
import { MenuItemsEditForm_menuItem$key } from '__generated__/MenuItemsEditForm_menuItem.graphql';

type FormFields = {
  name: string;
  type: MenuItemType | null;
  url: string;
  icon: string;
};

type Props = {
  menuItem: MenuItemsEditForm_menuItem$key;
};

const MenuItemsEditForm: FC<Props> = props => {
  const errorHandler = useErrorHandler();

  const { id, name, type, icon, url } = useFragment(
    graphql`
      fragment MenuItemsEditForm_menuItem on MenuItem {
        id
        name
        type
        icon
        url
      }
    `,
    props.menuItem
  );

  const [update, updating] = useMutation<MenuItemsEditFormMutation>(graphql`
    mutation MenuItemsEditFormMutation($id: ID!, $input: UpdateMenuItemInput!) {
      updateMenuItem(id: $id, input: $input) {
        ...EntitySidebar_entity
        ...MenuItemsEditForm_menuItem
      }
    }
  `);

  const initial = {
    name,
    type,
    url: url ?? '',
    icon: icon ?? '',
  };

  const handleSubmit = useCallback(
    (input: FormFields) => {
      update({
        variables: { id, input },

        ...errorHandler({
          successMessage: 'Išsaugota',
        }),
      });
    },
    [errorHandler, id, update]
  );

  return (
    <Formik<FormFields> initialValues={initial} onSubmit={handleSubmit}>
      <Form loading={updating}>
        <AccordionController id="menuItemEdit">
          <Section id="main" title="Bendra informacija">
            <Stack spacing={4}>
              <TextField autoFocus fullWidth name="name" label="Pavadinimas" />
              <MenuItemTypeSelect name="type" label="Tipas" />
              <TextField fullWidth name="url" label="Nuoroda" />
              <IconSelect name="icon" label="Ikona" />
            </Stack>
          </Section>
        </AccordionController>
      </Form>
    </Formik>
  );
};

export default MenuItemsEditForm;
